import classNames from 'classnames';
import { FC } from 'react';

interface Props {
  active?: boolean;
  backgroundColours?: string[];
  crossedOut?: boolean;
  diameter?: number;
  disabled?: boolean;
  text?: string;
  textStyle?: 'light' | 'dark';
}

export const VariantIconSvg: FC<Props> = ({
  active = false,
  backgroundColours,
  crossedOut = false,
  diameter = 36,
  disabled = false,
  text,
  textStyle = 'dark',
}) => {
  const radius = diameter / 2;
  const outerBorderWidth = 2;
  const innerBorderWidth = 2;
  const cx = radius;
  const cy = radius;

  return (
    <svg
      className="absolute left-0 top-0 h-full w-full"
      viewBox={`0 0 ${diameter} ${diameter}`}
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Outer border */}
      <circle
        className={classNames('transition-all duration-300', {
          'fill-neutral-6': active,
          'fill-neutral-2': !active,
          'group-hover:fill-neutral-4': !active && !disabled,
        })}
        cx={cx}
        cy={cy}
        r={radius}
      />

      {/* Inner border */}
      <circle
        className="fill-white"
        cx={cx}
        cy={cy}
        r={radius - outerBorderWidth}
      />

      {/* Background when there is one colour */}
      {backgroundColours?.length === 1 && (
        <circle
          cx={cx}
          cy={cy}
          fill={backgroundColours[0]}
          r={radius - outerBorderWidth - innerBorderWidth}
        />
      )}

      {/* Background when there are multiple colours */}
      {backgroundColours &&
        backgroundColours.length > 1 &&
        backgroundColours?.map((colour, i) => {
          const r = diameter / 2 - outerBorderWidth - innerBorderWidth;
          const angle = (2 * Math.PI) / backgroundColours.length;

          const x1 = cx;
          const y1 = cy;
          const x2 = cx + r * Math.sin(angle * (i + 1));
          const y2 = cy + -r * Math.cos(angle * (i + 1));
          const x3 = cx + r * Math.sin(angle * (i + 2));
          const y3 = cy + -r * Math.cos(angle * (i + 2));

          const command = `M ${x1}, ${y1} L ${x2},${y2} A ${r} ${r}, 0, 0 1, ${x3},${y3}`;

          return <path key={i} d={command} fill={colour} />;
        })}

      {/* Crossed out */}
      {crossedOut && (
        <line
          className="stroke-neutral-3"
          strokeWidth={2}
          x1={cx + (cx - 2) * Math.sin(0.25 * Math.PI)}
          x2={cx + (cx - 2) * Math.sin(1.25 * Math.PI)}
          y1={cy + (-cy + 2) * Math.cos(0.25 * Math.PI)}
          y2={cy + (-cy + 2) * Math.cos(1.25 * Math.PI)}
        />
      )}

      {/* Text */}
      {text && (
        <text
          alignmentBaseline="central"
          className={classNames({
            'fill-neutral-6': textStyle === 'dark',
            'fill-white': textStyle === 'light',
          })}
          textAnchor="middle"
          x={cx}
          y={cy}
        >
          {text}
        </text>
      )}
    </svg>
  );
};
